import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '@mm-aggregator/drf-dynamics';

import { MarketCurrencySummary } from './financial-entity-balance.service';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

function unwrapList<T>(payload: T[] | PaginatedResponse<T>): T[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  return payload.results ?? [];
}

@Injectable({ providedIn: 'root' })
export class MarketCurrencyService {
  private readonly api = inject(ApiService);

  list(params?: Record<string, string>): Observable<MarketCurrencySummary[]> {
    return this.api
      .get<MarketCurrencySummary[] | PaginatedResponse<MarketCurrencySummary>>('finances/market-currencies/', params)
      .pipe(map(unwrapList));
  }

  /** Build canonical market slug, e.g. USD + CD → USD_CD. */
  buildMarketCode(currencyCode: string, countryCode: string): string {
    return `${currencyCode}_${countryCode}`;
  }

  resolveMarket(currencyCode: string, countryCode: string): Observable<MarketCurrencySummary | null> {
    const fallbackCode = this.buildMarketCode(currencyCode, countryCode);
    return this.list({ search: fallbackCode, page_size: '10' }).pipe(
      map((markets) => {
        const match = markets.find((m) => m.currency_code === currencyCode && m.country_code === countryCode);
        return match ?? markets.find((m) => m.code === fallbackCode) ?? null;
      }),
    );
  }

  resolveMarketCode(currencyCode: string, countryCode: string): Observable<string> {
    const fallback = this.buildMarketCode(currencyCode, countryCode);
    return this.list({ search: fallback, page_size: '10' }).pipe(
      map((markets) => {
        const match = markets.find((m) => m.currency_code === currencyCode && m.country_code === countryCode);
        return match?.code || fallback;
      })
    );
  }
}
